import { useCallback, useState } from 'react';
import './RetailOrderGrid.scss';
import {
    Column,
    Format,
    Item,
    Lookup
} from 'devextreme-react/data-grid';

import { useGetRetailOrders } from "../api/get-retail-orders";
import { RetailOrderGridProps } from '../types/RetailOrderGridProps';
import { useGetOrderStatuses } from '../../../../api/lookup';
import { Link, useLocation } from 'react-router-dom';
import { LoadPagePanel, SyncDataGrid } from '../../../../components';
import { ClosedOrdersControl } from '../../../common';
import OrderTypeSelector from '../../order-type-selector/components/OrderTypeSelector';
import { useAuth } from '../../../../contexts/auth';
import { useGetCompany } from '../../../../api/company';

export default function RetailOrderGrid(props: RetailOrderGridProps) {

    const { user } = useAuth();
    const company = useGetCompany(user?.companyId);

    let [closedOrdersState, setClosedOrdersState] = useState<{ closedOrders: boolean, dateFrom: Date | null }>
        ({ closedOrders: false, dateFrom: null });

    const orders = useGetRetailOrders(props.vanityOrders, props.backOrders, closedOrdersState.closedOrders, closedOrdersState.dateFrom);
    const orderStatuses = useGetOrderStatuses();

    const location = useLocation();
    const returnUrl = encodeURIComponent(location.pathname + location.search);

    const isIME = !!company.data?.assignedRetailers.find(x => x.IME);

    const rowPrepared = useCallback((e: any) => {
        if (e.data !== undefined) {
            if (e.data.isQuickShip) {
                e.rowElement.classList.add("quick-ship-order-highlighted");
            }
        }
    }, []);

    const roNumberCellRender = useCallback((cell: any) => {
        return <Link to={`/cfi/retail-order/${cell.data.retailOrderID}?returnUrl=${returnUrl}`}>
            {cell.data.roNumber}
        </Link>;
    }, [returnUrl]);

    return (
        <div className='dx-card wide-card'>

            <OrderTypeSelector />

            <SyncDataGrid
                stateStorageKey={'retail-order-grid-v2-' + props.vanityOrders + '-' + props.backOrders}
                dataSource={orders.data}
                keyExpr={'retailOrderID'}
                height="60vh"
                additionalToolbarItems={[
                    <Item
                        location={'before'}
                        key="closed-orders"
                    >
                        <ClosedOrdersControl
                            onClosedOrdersChanged={(e) => {
                                setClosedOrdersState({ closedOrders: e.checked, dateFrom: e.dateFrom })
                            }}
                        />
                    </Item >
                ]}
                onRowPrepared={rowPrepared}
            >
                <Column
                    dataField={'roNumber'}
                    caption={'Order #'}
                    dataType="string"
                    cellRender={roNumberCellRender}
                />
                <Column
                    dataField={'roType'}
                    caption={'Order Type'}
                    dataType="string"
                />
                <Column
                    dataField={'projectNumber'}
                    caption={'Project #'}
                    dataType="string"
                />
                <Column
                    dataField={'roDate'}
                    caption={'Customer Order Date'}
                    dataType="date"
                />
                <Column
                    dataField={'amount'}
                    caption={'Order Amount'}
                    dataType="number"
                    editorOptions={{ format: '$#,##0.##', showClearButton: true }}
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
                {
                    company.data?.SFI &&
                    <Column
                        dataField={'sfi'}
                        caption={'SF&I Lead'}
                        dataType="boolean"
                    />
                }
                <Column
                    dataField={'cabinetDate'}
                    caption={'Cabinet Install Date'}
                    dataType="date"
                />
                <Column
                    dataField={'dealerNumber'}
                    caption={'Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'proxyStoreNumber'}
                    caption={'Proxy Store #'}
                    dataType="string"
                />
                <Column
                    dataField={'orderStatus'}
                    caption={'Order Status'}
                    dataType="string"
                >
                    <Lookup
                        dataSource={orderStatuses.data}
                        valueExpr={'description'}
                        displayExpr={'description'}
                    />
                </Column>
                <Column
                    dataField={'productLine'}
                    caption={'Product Line'}
                    dataType="string"
                />
                <Column
                    dataField={'customerName'}
                    caption={'Customer Name'}
                    dataType="string"
                />
                <Column
                    dataField={'customerAddress'}
                    caption={'Customer Address'}
                    dataType="string"
                />
                <Column
                    dataField={'customerPhone'}
                    caption={'Customer Phone #'}
                    dataType="string"
                />
                <Column
                    dataField={'customerEmail'}
                    caption={'Customer Email'}
                    dataType="string"
                />
                <Column
                    dataField={'customerCityZip'}
                    caption={'City/State,Zip'}
                    dataType="string"
                />
                {
                    isIME &&
                    <Column
                        dataField={'imeStatus'}
                        caption={'IME Status'}
                        dataType="string"
                    />
                }
                <Column
                    dataField={'cfiDatePaid'}
                    caption={'Date Paid'}
                    dataType="date"
                />
                <Column
                    dataField={'projectManager'}
                    caption={'Project Manager'}
                    dataType="string"
                />
            </SyncDataGrid>

            <LoadPagePanel visible={orders.isLoading} />
        </div>
    )
} 
